// frontend/src/pages/Proveedores.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Button,
  Stack,
  Typography,
  Paper,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DataTable from '../components/DataTable';
import AppPagination from '../components/AppPagination';
import { fetchProveedores, deleteProveedor } from '../api/proveedores';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';

export default function Proveedores() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Paginación que recibe el API (1-based).
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const searchDebounce = useRef(null);

  const nav = useNavigate();
  const { start, stop } = useLoading();
  const { showToast } = useToast();
  const confirm = useConfirm();

  const columns = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Nombre', accessor: 'nombre' },
    { Header: 'Contacto', accessor: 'contacto' },
  ];

  const loadProveedores = async () => {
    setLoading(true);
    try {
      const res = await fetchProveedores({ page, limit, search });
      const payload = res?.data ?? {};
      const data = Array.isArray(payload?.data) ? payload.data : [];
      setRows(data);
      setTotal(Number(payload?.total ?? 0));
    } catch (err) {
      console.error('Error al cargar proveedores', err);
      showToast({ message: 'Error al cargar proveedores', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadProveedores();
      } finally {
        stop();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      setPage(1);
      await loadProveedores();
    }, 300);
    return () => clearTimeout(searchDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const goToNuevo = () => {
    start();
    nav('/proveedores/nuevo');
  };

  const goToEditar = (id) => {
    start();
    nav(`/proveedores/editar/${id}`);
  };

  const handleDelete = async (row) => {
    const ok = await confirm({
      title: 'Eliminar proveedor',
      content: `¿Eliminar el proveedor "${row.nombre}"?`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmColor: 'error',
    });
    if (!ok) return;

    start();
    try {
      await deleteProveedor(row.id);
      showToast({ message: 'Proveedor eliminado', severity: 'success' });

      const nextPage = page > 1 && rows.length === 1 ? page - 1 : page;
      setPage(nextPage);
      await loadProveedores();
    } catch (err) {
      console.error('Error al eliminar proveedor', err);
      showToast({ message: 'No se pudo eliminar el proveedor', severity: 'error' });
    } finally {
      stop();
    }
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
        <Typography variant="h5">Proveedores</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar por nombre o contacto"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={goToNuevo}>
            Nuevo proveedor
          </Button>
        </Stack>
      </Stack>

      <Paper>
        <DataTable
          rows={rows}
          columns={columns}
          loading={loading}
          onEdit={(row) => goToEditar(row.id)}
          onDelete={handleDelete}
        />

        <AppPagination
          count={total}
          page={Math.max(0, page - 1)}
          rowsPerPage={limit}
          onPageChange={(_event, nextPage) => setPage(nextPage + 1)}
          onRowsPerPageChange={(event) => {
            setPage(1);
            setLimit(Number(event.target.value));
          }}
        />
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Total: {total}
      </Typography>
    </Container>
  );
}
